/**
 * Builds the multi-sheet Excel/Word/PDF export for the ATO & SRO agent.
 *
 * The on-screen summary surfaces far more than the old flat "Field / Value"
 * dump: per-authority running/company balances and flags, the rebuilt RBA debt
 * breakdown, every reported balance ("tie"), and the cleaned ledger movements.
 * This builder mirrors that, one sheet per view:
 *   - "Authorities"      — one row per authority (the top table). Omitted
 *                          from Excel: notes and concatenated flags make that
 *                          sheet unusable as a spreadsheet. Still in Word/PDF.
 *   - "Debt Breakdown"   — GST / PAYG / interest / penalty / payments per
 *                          rebuilt RBA ledger (the debt-breakdown cards).
 *   - "Balance Sources"  — every reported figure for each authority (the ties).
 *   - "Flags"            — one row per flag detail line.
 *   - "Ledger Movements" — every cleaned RBA movement (the expandable table).
 *
 * Money is written as real numbers and dates as real Date cells so the
 * practitioner can sort / sum / filter natively.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import type { AtoSroEntryUI } from "./types";

export interface AtoLedgerRow {
  _id: string;
  atoSroEntryId: string;
  effectiveDate: string;
  processDate?: string;
  description: string;
  debit: number;
  credit: number;
  rebuiltBalance: number;
  category: string;
  kind: "movement" | "breakdown_child" | "detail";
  /** Which ATO account the row came from (ICA / ITA); legacy rows unset. */
  accountType?: string;
}

/** Short labels for the ATO account a ledger belongs to (MOR-875). */
const ACCOUNT_LABELS: Record<string, string> = {
  ica: "ICA",
  ita: "ITA",
  other: "Other",
};

const FLAG_TYPE_LABELS: Record<string, string> = {
  non_matching_balances: "Non-matching balances",
  round_payments: "Round payments",
  payment_plan: "Payment plan",
  warning_letters: "Warning letters",
  dpn_issued: "DPN issued",
  garnishee_notice: "Garnishee notice",
  late_lodgement: "Late lodgement",
  other: "Other",
};

const FLAG_SEVERITY: Record<string, string> = {
  dpn_issued: "Critical",
  garnishee_notice: "Critical",
  non_matching_balances: "Warning",
  round_payments: "Warning",
  payment_plan: "Info",
  warning_letters: "Warning",
  late_lodgement: "Warning",
  other: "Info",
};

const BALANCE_SOURCE_LABELS: Record<string, string> = {
  rba_ledger: "ATO RBA (rebuilt)",
  proof_of_debt: "Proof of Debt",
  ato_statement: "ATO statement",
  director_gl: "Director GL",
  other: "Other",
};

const CATEGORY_LABELS: Record<string, string> = {
  gst: "GST",
  payg: "PAYG withholding",
  income_tax: "Income tax",
  interest: "Interest (GIC)",
  penalty: "Penalties",
  payment: "Payments applied",
  statement: "Statement",
  other: "Other",
};

function authorityShort(authority: string): string {
  if (authority === "ato") return "ATO";
  if (authority.startsWith("sro_")) return "SRO";
  return "Other";
}

/**
 * The authoritative running balance for an authority: the practitioner-selected
 * figure, else the rebuilt RBA ledger closing, else the statement-derived
 * running balance (mirrors getEffectiveBalance in AtoSroPanel).
 */
function effectiveBalance(e: AtoSroEntryUI): number | undefined {
  return (
    e.selectedBalance?.amount ??
    e.ledger?.closingBalance ??
    e.runningBalance?.amount
  );
}

/** Parse YYYY-MM-DD into a real Date so Excel treats it as a date; else raw. */
function parseDateCell(raw?: string): Date | string {
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number) as [number, number, number];
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? raw : date;
  }
  return raw;
}

function authoritiesSheet(entries: AtoSroEntryUI[]): ExportSheet {
  const rows: ExportSheet["rows"] = entries.map((e) => {
    const running = effectiveBalance(e);
    const company = e.companyBalance?.amount;
    const variance =
      running != null && company != null ? Math.abs(running - company) : "";
    const balanceSource = e.selectedBalance
      ? e.selectedBalance.source === "manual"
        ? "Manual override"
        : (e.selectedBalance.label ?? e.selectedBalance.source)
      : e.ledger
        ? "ATO RBA (rebuilt)"
        : (e.runningBalance?.source ?? "");
    const flagLabels = e.flags
      .map((f) => FLAG_TYPE_LABELS[f.type] ?? f.type)
      .join(", ");
    return [
      authorityShort(e.authority),
      e.authorityLabel,
      running ?? "",
      balanceSource,
      parseDateCell(
        e.selectedBalance?.asAtDate ??
          e.ledger?.asAtDate ??
          e.runningBalance?.asAtDate,
      ),
      company ?? "",
      variance,
      e.ledger?.closingBalance ?? "",
      e.totalBankPayments ?? "",
      e.flags.length,
      flagLabels,
      e.sourceDocuments.length,
      e.notes ?? "",
    ];
  });
  return {
    name: "Authorities",
    omitFromExcel: true,
    headers: [
      "Authority",
      "Authority Name",
      "Running Balance",
      "Balance Source",
      "As At",
      "Company Balance",
      "Variance",
      "Net Owing (RBA)",
      "Bank Payments",
      "Flag Count",
      "Flags",
      "Documents",
      "Notes",
    ],
    rows,
  };
}

function debtBreakdownSheet(entries: AtoSroEntryUI[]): ExportSheet | null {
  const rows: ExportSheet["rows"] = [];
  const push = (
    e: AtoSroEntryUI,
    account: string,
    ledger: NonNullable<AtoSroEntryUI["ledger"]>,
  ) => {
    const t = ledger.categoryTotals;
    rows.push([
      authorityShort(e.authority),
      e.authorityLabel,
      account,
      t.gst ?? 0,
      t.payg ?? 0,
      t.incomeTax ?? 0,
      t.interest ?? 0,
      t.penalty ?? 0,
      t.payment ?? 0,
      t.other ?? 0,
      ledger.closingBalance,
      parseDateCell(ledger.asAtDate),
      ledger.reconciledToPod === "matched"
        ? "Matched"
        : ledger.reconciledToPod === "variance"
          ? "Variance"
          : "No PoD",
      ledger.podVariance ?? "",
      ledger.rowCount,
    ]);
  };
  for (const e of entries) {
    // One row per rebuilt account (ICA / ITA), then the consolidated roll-up
    // when there is more than one account to consolidate (MOR-875).
    const accounts = e.ledgers ?? [];
    for (const a of accounts) {
      push(
        e,
        a.accountLabel ?? ACCOUNT_LABELS[a.accountType] ?? a.accountType,
        a,
      );
    }
    if (e.ledger && accounts.length !== 1) {
      push(e, accounts.length > 1 ? "Consolidated" : "", e.ledger);
    }
  }
  if (rows.length === 0) return null;
  return {
    name: "Debt Breakdown",
    headers: [
      "Authority",
      "Authority Name",
      "Account",
      "GST",
      "PAYG Withholding",
      "Income Tax",
      "Interest (GIC)",
      "Penalties",
      "Payments Applied",
      "Other",
      "Net Owing",
      "As At",
      "Reconciled to PoD",
      "PoD Variance",
      "Movements",
    ],
    rows,
  };
}

function balanceSourcesSheet(entries: AtoSroEntryUI[]): ExportSheet | null {
  const rows: ExportSheet["rows"] = [];
  for (const e of entries) {
    const seen = new Set<number>();
    const add = (
      sourceType: string,
      label: string,
      amount: number,
      asAtDate?: string,
    ) => {
      const k = Math.round(amount);
      if (seen.has(k)) return;
      seen.add(k);
      rows.push([
        authorityShort(e.authority),
        e.authorityLabel,
        BALANCE_SOURCE_LABELS[sourceType] ?? sourceType,
        label,
        amount,
        parseDateCell(asAtDate),
      ]);
    };
    for (const o of e.balanceObservations ?? []) {
      add(
        o.sourceType,
        o.label ?? BALANCE_SOURCE_LABELS[o.sourceType] ?? o.sourceType,
        o.amount,
        o.asAtDate,
      );
    }
    if (e.runningBalance) {
      add(
        "ato_statement",
        e.runningBalance.source || "Statement balance",
        e.runningBalance.amount,
        e.runningBalance.asAtDate,
      );
    }
    if (e.companyBalance) {
      add(
        "director_gl",
        e.companyBalance.source || "Company GL",
        e.companyBalance.amount,
        e.companyBalance.asAtDate,
      );
    }
  }
  if (rows.length === 0) return null;
  return {
    name: "Balance Sources",
    headers: [
      "Authority",
      "Authority Name",
      "Source Type",
      "Label",
      "Amount",
      "As At",
    ],
    rows,
  };
}

function flagsSheet(entries: AtoSroEntryUI[]): ExportSheet | null {
  const rows: ExportSheet["rows"] = [];
  for (const e of entries) {
    for (const flag of e.flags) {
      const label = FLAG_TYPE_LABELS[flag.type] ?? flag.type;
      const severity = FLAG_SEVERITY[flag.type] ?? "Info";
      if (flag.details.length === 0) {
        rows.push([
          authorityShort(e.authority),
          e.authorityLabel,
          label,
          severity,
          "",
        ]);
      } else {
        for (const detail of flag.details) {
          rows.push([
            authorityShort(e.authority),
            e.authorityLabel,
            label,
            severity,
            detail,
          ]);
        }
      }
    }
  }
  if (rows.length === 0) return null;
  return {
    name: "Flags",
    headers: ["Authority", "Authority Name", "Flag", "Severity", "Detail"],
    rows,
  };
}

function ledgerMovementsSheet(
  entries: AtoSroEntryUI[],
  ledgerRows: AtoLedgerRow[],
): ExportSheet | null {
  const movements = ledgerRows.filter((r) => r.kind === "movement");
  if (movements.length === 0) return null;

  const labelByEntry = new Map<string, string>();
  for (const e of entries) labelByEntry.set(e._id, authorityShort(e.authority));

  const sorted = [...movements].sort((a, b) => {
    const byEntry = a.atoSroEntryId.localeCompare(b.atoSroEntryId);
    if (byEntry !== 0) return byEntry;
    const byAccount = (a.accountType ?? "").localeCompare(b.accountType ?? "");
    if (byAccount !== 0) return byAccount;
    return a.effectiveDate.localeCompare(b.effectiveDate);
  });

  const rows: ExportSheet["rows"] = sorted.map((r) => [
    labelByEntry.get(r.atoSroEntryId) ?? "",
    r.accountType ? (ACCOUNT_LABELS[r.accountType] ?? r.accountType) : "",
    parseDateCell(r.effectiveDate),
    parseDateCell(r.processDate),
    r.description,
    CATEGORY_LABELS[r.category] ?? r.category,
    r.debit || "",
    r.credit || "",
    r.rebuiltBalance,
  ]);

  return {
    name: "Ledger Movements",
    headers: [
      "Authority",
      "Account",
      "Effective Date",
      "Process Date",
      "Description",
      "Category",
      "Debit",
      "Credit",
      "Balance",
    ],
    rows,
  };
}

export function buildAtoSroExport(
  entries: AtoSroEntryUI[],
  ledgerRows: AtoLedgerRow[],
): ExportContent {
  const sheets: ExportSheet[] = [authoritiesSheet(entries)];
  const debt = debtBreakdownSheet(entries);
  if (debt) sheets.push(debt);
  const sources = balanceSourcesSheet(entries);
  if (sources) sheets.push(sources);
  const flags = flagsSheet(entries);
  if (flags) sheets.push(flags);
  const movements = ledgerMovementsSheet(entries, ledgerRows);
  if (movements) sheets.push(movements);

  return { title: "ATO & SRO", sheets };
}
